import type { Editor } from '@tiptap/core';
import classNames from 'classnames';
import { camelCase, upperFirst } from 'lodash';
import type { CSSProperties } from 'react';
import React from 'react';
import type { GIFData, ImageData, PluginContainerData } from 'ricos-schema';
import {
  Node_Type,
  PluginContainerData_Alignment,
  PluginContainerData_Width_Type,
} from 'ricos-schema';
import type {
  ExtensionProps,
  GeneralContext,
  RicosExtension,
  RicosExtensionConfig,
  RicosServices,
} from 'ricos-types';
import generalRTLIgnoredStyles from 'wix-rich-content-common/dist/statics/styles/general.rtlignore.scss';
import generalStyles from 'wix-rich-content-editor-common/dist/statics/styles/general.scss';

import { createRicosFunctionalExtension } from './create-ricos-extension';

const stylesWithRTL = { ...generalStyles, ...generalRTLIgnoredStyles };
export const getAlignmentClassName = (alignment?: PluginContainerData_Alignment) => {
  if (!alignment || alignment === PluginContainerData_Alignment.UNRECOGNIZED) {
    return '';
  }
  const key = `align${upperFirst(alignment.toLowerCase())}`;
  return stylesWithRTL[key];
};

export const getSizeClassName = (size?: PluginContainerData_Width_Type) => {
  if (!size || size === PluginContainerData_Width_Type.UNRECOGNIZED) {
    return '';
  }
  const key = `size${upperFirst(camelCase(size))}`;

  return stylesWithRTL[key];
};

export const getFocusClassName = (isFocused?: boolean) => {
  return classNames({
    [stylesWithRTL.hasFocus]: isFocused,
    [stylesWithRTL.hideTextSelection]: !isFocused,
  });
};

export const getTextWrapClassName = (textWrap?: boolean) => {
  if (textWrap === false) {
    return stylesWithRTL.textWrapNowrap;
  }
  return '';
};

export const getPluginContainerClassName = (isMobile: boolean) => {
  return classNames(stylesWithRTL.pluginContainer, {
    [stylesWithRTL.pluginContainerMobile]: isMobile,
  });
};

export const getComponentStyles = ({
  componentData,
  isFocused,
  isMobile,
  isTextWrap,
  shouldShowSelection,
}: {
  componentData: { containerData: PluginContainerData };
  isFocused: boolean;
  isMobile: boolean;
  isTextWrap: boolean;
  shouldShowSelection: boolean;
}) => {
  const alignment = componentData?.containerData?.alignment;
  const size = componentData?.containerData?.width?.size;
  const textWrap = isTextWrap && componentData?.containerData?.textWrap;
  return {
    alignmentClassName: getAlignmentClassName(alignment),
    sizeClassName: getSizeClassName(size),
    focusClassName: shouldShowSelection ? getFocusClassName(isFocused) : false,
    textWrapClassName: getTextWrapClassName(textWrap),
    pluginContainerClassName: shouldShowSelection ? getPluginContainerClassName(isMobile) : false,
  };
};

const originalSizeMapper = {
  [Node_Type.IMAGE]: (componentData: ImageData) => componentData.image?.width,
  [Node_Type.GIF]: (componentData: GIFData) => componentData.width,
};

const disableCustomWidthMapper = {
  [Node_Type.IMAGE]: (componentData: ImageData, isMobile: boolean) =>
    componentData?.image?.width &&
    isMobile &&
    (componentData?.containerData?.width?.custom || 0) > 150,
};

const getStylesHOC =
  (isTextWrap: boolean, context: Omit<GeneralContext, 'portal'>, nodesWithoutSelection: string[]) =>
  (Component: React.FC<any>) => {
    const Styles = (props: {
      componentData: { containerData: PluginContainerData };
      node: { attrs: { id: string }; type: { name: string } };
      editor: Editor;
    }) => {
      const { componentData, node, editor } = props;
      const nodeId = node.attrs.id;
      const { isMobile } = context;
      const { selection } = editor.state;
      const shouldShowSelection = !nodesWithoutSelection.includes(node.type.name);
      let isFocused = false;

      if (shouldShowSelection) {
        editor.state.doc.nodesBetween(selection.from, selection.to, node => {
          node.attrs.id === nodeId && (isFocused = true);
        });
      }

      const componentStyles = getComponentStyles({
        componentData,
        isFocused,
        isMobile,
        isTextWrap,
        shouldShowSelection,
      });

      const disableCustomWidth =
        disableCustomWidthMapper[node.type.name]?.(componentData, isMobile) ?? false;

      const customWidth = !disableCustomWidth && componentData?.containerData?.width?.custom;
      const originalWidth =
        (componentData?.containerData?.width?.size === 'ORIGINAL' || disableCustomWidth) &&
        originalSizeMapper[node.type.name]?.(componentData);
      const width = customWidth || originalWidth;
      const style: CSSProperties = {
        width: width && `min(100%, ${width}px)`,
      };

      return (
        <div className={Object.values(componentStyles).join(' ')} style={style}>
          <Component {...props} />
        </div>
      );
    };
    Styles.displayName = 'StylesHoc';

    return Styles;
  };

export const styles = createRicosFunctionalExtension({
  type: 'extension',
  groups: [],
  name: 'style',
  reconfigure: (
    config: RicosExtensionConfig,
    extensions: RicosExtension[],
    ricosProps: ExtensionProps,
    _settings: Record<string, unknown>,
    services: RicosServices
  ) => ({
    ...config,
    addNodeHoc() {
      return {
        nodeTypes: extensions
          .filter(extension => !extension.groups.includes('custom-styles'))
          .map(({ name }) => name),
        priority: 100,
        nodeHoc: getStylesHOC(
          ricosProps.isTextWrap ?? true,
          services.context,
          extensions
            .filter(({ groups }) => groups.includes('hide-selection'))
            .map(({ name }) => name)
        ),
      };
    },
  }),
  createExtensionConfig() {
    return {
      name: this.name,
      priority: 30,
    };
  },
});
